import { Container, ContainerGutter, ContainerPadding, CTA, Richtext, styled, Text, TokenTextAppearance, TextTag } from '@volkswagen-onehub/components-core';
import { Checkmark } from '@volkswagen-onehub/icons-core';
import { FocusLayerSizeV2 } from '@volkswagen-onehub/layer-manager';
import { FeatureHighlight } from '@vw-marketing/vw-ca-types';
import React, { useContext, useEffect, useState } from 'react';
import { LayerContext, useFocusLayer } from '../Layers';
import { getLang, getTranslations } from '../translations';
import { VehicleData } from '../types';
import { VehicleCategory } from '../types/enums/vehicles';
import { FullDescription } from './VehicleCard/FullDescription';

interface FeatureHighlighsInterface {
    category: VehicleData['category'],
    features: VehicleData['feature_highlights'],
    description: VehicleData['description']
}

const StyledList = styled.ul`
 list-style: none;
  padding: 0px;
  margin: 0px;
  display: flex;
  flex-flow: column;
  gap: 8px;
`;

const StyledListItem = styled.li`
  color: #000;
  display: grid;
  -moz-box-align: baseline;
  align-items: baseline;
  grid-template-columns: max-content 1fr;
  column-gap: 12px;
  span {
    line-height: 125%;
  }
`;

const RichTextContainer = styled(Text)``;

const FeatureHighlights = ({ category, features, description }: React.PropsWithChildren<FeatureHighlighsInterface>) => {

    const lang = getLang();
    const { labels } = getTranslations();
    const [showMore, setShowMore] = useState<boolean | null>(null);
    const [highlights, setHightlights] = useState<FeatureHighlight | null>(null);
    const [desc, setDesc] = useState<string | null>(null);

    const { closeLayer } = useContext(LayerContext)

    const openFullDescriptionLayer = useFocusLayer('vehicle-description-layer', {
      size: FocusLayerSizeV2.A,
      onClose: () => closeLayer('vehicle-description-layer'),
      userCloseable: true
    })

    const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault();
        openFullDescriptionLayer((close) => <FullDescription close={close} header={labels.features_and_details} category={category} highlights={highlights as string[]} desc={desc}/>)
    };

    useEffect(() => {
        if(!category) return;

        if(category == VehicleCategory.NEW) {
            const highlightsData = features[lang];

            if(highlightsData) {
                let data = highlightsData;

                if(data.length > 6 && !showMore) {
                    setShowMore(false);
                }

                setHightlights(data);
            }
        }
        else if(category == VehicleCategory.USED || category == VehicleCategory.CERTIFIED) {
            if(description && description[lang]) {
                let descriptionData = description[lang];

                if(descriptionData && descriptionData.length > 150 && !showMore) {
                    setShowMore(false);
                }

                if(descriptionData) {
                    descriptionData = descriptionData.replace(/\*/g, '');
                }

                setDesc(descriptionData)
            }
        }
    }, [category, features, showMore]);

    return (
        <Container
            wrap="always">

            {category == VehicleCategory.NEW &&
                <Container padding={{left: ContainerPadding.static350}} gutter={ContainerGutter.dynamic0040} wrap="always">
                    {highlights?.length && <StyledList>
                            {
                                highlights && highlights.length && highlights.slice(0, 6).map((featureHighlight, i) => {
                                  const key = `feature-highlight-${i}`;
                                    return (
                                        <StyledListItem key={key}>
                                          <Checkmark variant="small" />
                                          <Text tag={TextTag.span} appearance={TokenTextAppearance.copy200}>
                                            <Richtext markdown={featureHighlight} dangerouslyParseRawHTML />
                                          </Text>
                                        </StyledListItem>
                                    );
                                })
                            }
                        </StyledList>
                    }
                </Container>
            }

            {category != VehicleCategory.NEW &&
                <Container>
                    {desc && desc.length &&
                        <RichTextContainer>
                            <Richtext markdown={desc.substring(0, 300)} dangerouslyParseRawHTML />&nbsp;<Text tag={TextTag.span}>...</Text>
                        </RichTextContainer>
                    }
                </Container>
            }

                { showMore!= null &&
                    <Container padding={{top: ContainerPadding.static150}}>
                        <CTA tag="button" emphasis="tertiary" onClick={e => handleClick(e)}>
                            <Text tag={TextTag.span} bold>{ labels.show_more }</Text>
                        </CTA>
                    </Container>
                }
        </Container>
    );
}

export default FeatureHighlights;
